import React, { useState, useEffect } from 'react';
import { useFirebase } from '../../../context/FirebaseContext';
import { usePipelineWebhookEnabled } from '../../../context/FeatureFlagContext';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { updatePipelineWebhook, regeneratePipelineWebhookSecret } from '../../../api/conceptgate';
import { getApiOrigin } from '../../../api/core';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WebhookIcon from '@mui/icons-material/Webhook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import './PipelineSettingsModal.css';

const PipelineSettingsModal = ({ pipeline, open, onClose }) => {
  const { auth } = useFirebase();
  const { updatePipelineInfo, loadPipelines } = useConceptGate();
  const webhookFeatureEnabled = usePipelineWebhookEnabled();

  const [name, setName] = useState(pipeline?.name || '');
  const [nameError, setNameError] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  const [webhookEnabled, setWebhookEnabled] = useState(pipeline?.webhookEnabled || false);
  const [webhookUrl, setWebhookUrl] = useState(pipeline?.webhookUrl || '');
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // URL query parameter options
  const [includeDetails, setIncludeDetails] = useState(false);
  const [failfast, setFailfast] = useState(true);

  // Reset state when pipeline changes
  useEffect(() => {
    if (pipeline) {
      setName(pipeline.name || '');
      setWebhookEnabled(pipeline.webhookEnabled || false);
      setWebhookUrl(pipeline.webhookUrl || '');
    }
  }, [pipeline]);

  // Reset success state after delay
  useEffect(() => {
    if (nameSuccess) {
      const timer = setTimeout(() => setNameSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [nameSuccess]);

  if (!open || !pipeline) return null;

  const handleNameSave = async () => {
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }
    if (name.trim() === pipeline.name) {
      return; // No change
    }

    setNameSaving(true);
    setNameError(null);

    try {
      const result = await updatePipelineInfo(pipeline.id, { name: name.trim() });
      if (result) {
        setNameSuccess(true);
      } else {
        setNameError('Failed to update name');
      }
    } catch (e) {
      setNameError(e.message || 'Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  const handleToggleWebhook = async () => {
    if (webhookLoading) return;
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await updatePipelineWebhook(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookEnabled(result.webhookEnabled);
          setWebhookUrl(result.webhookUrl);
          if (loadPipelines) loadPipelines();
        }
      }, pipeline.id, !webhookEnabled);
    } catch (e) {
      setWebhookError(e.message || 'Failed to update webhook settings');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (webhookLoading) return;
    if (!window.confirm('Are you sure you want to regenerate the webhook secret? This will invalidate the current URL.')) {
      return;
    }
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await regeneratePipelineWebhookSecret(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookUrl(result.webhookUrl);
          if (loadPipelines) loadPipelines();
        }
      }, pipeline.id);
    } catch (e) {
      setWebhookError(e.message || 'Failed to regenerate secret');
    } finally {
      setWebhookLoading(false);
    }
  };

  const getWebhookUrl = (withQueryParams = true) => {
    if (!webhookUrl) return '';
    // If backend returns relative URL, prepend the API origin
    let url = webhookUrl.startsWith('/') ? `${getApiOrigin()}${webhookUrl}` : webhookUrl;

    // Add query parameters if enabled
    if (withQueryParams) {
      const params = [];
      if (includeDetails) params.push('details=true');
      if (failfast) params.push('failfast=true');
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
    }
    return url;
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(getWebhookUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setWebhookError('Failed to copy to clipboard');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="pipeline-settings-overlay" onClick={handleOverlayClick}>
      <div className="pipeline-settings-modal">
        <div className="pipeline-settings-header">
          <h3>Pipeline Settings</h3>
          <button className="pipeline-settings-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="pipeline-settings-content">
          {/* Name Section */}
          <div className="pipeline-settings-section">
            <label className="pipeline-settings-label">Pipeline Name</label>
            <div className="pipeline-name-input-row">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNameSave();
                }}
                className={`pipeline-name-input ${nameError ? 'error' : ''}`}
                placeholder="Enter pipeline name"
              />
              <button
                className={`pipeline-name-save ${nameSuccess ? 'success' : ''}`}
                onClick={handleNameSave}
                disabled={nameSaving || name.trim() === pipeline.name}
              >
                {nameSaving ? '...' : nameSuccess ? <CheckIcon fontSize="small" /> : 'Save'}
              </button>
            </div>
            {nameError && <div className="pipeline-settings-error">{nameError}</div>}
          </div>

          {/* Webhook Section */}
          {webhookFeatureEnabled && (
            <div className="pipeline-settings-section webhook-section">
              <div className="webhook-header">
                <WebhookIcon className="webhook-icon" />
                <label className="pipeline-settings-label">Webhook Integration</label>
                <button
                  className="webhook-help-btn"
                  onClick={() => setShowHelp(!showHelp)}
                  title="Help"
                >
                  <HelpOutlineIcon fontSize="small" />
                </button>
              </div>

              {webhookError && <div className="pipeline-settings-error">{webhookError}</div>}

              <div className="webhook-toggle-row">
                <span className="webhook-toggle-label">
                  {webhookEnabled ? 'Webhook enabled' : 'Webhook disabled'}
                </span>
                <button
                  className={`webhook-toggle ${webhookEnabled ? 'enabled' : ''}`}
                  onClick={handleToggleWebhook}
                  disabled={webhookLoading}
                >
                  <span className="webhook-toggle-slider" />
                </button>
              </div>

              {webhookEnabled && (
                <div className="webhook-url-section">
                  <label className="webhook-url-label">Webhook URL</label>
                  <div className="webhook-url-row">
                    <input
                      type="text"
                      value={getWebhookUrl()}
                      readOnly
                      className="webhook-url-input"
                    />
                    <button
                      className={`webhook-url-btn ${copied ? 'copied' : ''}`}
                      onClick={handleCopyUrl}
                      title="Copy URL"
                    >
                      {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </button>
                    <button
                      className="webhook-url-btn regenerate"
                      onClick={handleRegenerateSecret}
                      disabled={webhookLoading}
                      title="Regenerate secret"
                    >
                      <RefreshIcon fontSize="small" />
                    </button>
                  </div>

                  {/* URL Options */}
                  <div className="webhook-options">
                    <label className="webhook-option">
                      <input
                        type="checkbox"
                        checked={includeDetails}
                        onChange={(e) => setIncludeDetails(e.target.checked)}
                      />
                      <span className="webhook-option-text">
                        <strong>details</strong> - Include detailed rule execution results
                      </span>
                    </label>
                    <label className="webhook-option">
                      <input
                        type="checkbox"
                        checked={failfast}
                        onChange={(e) => setFailfast(e.target.checked)}
                      />
                      <span className="webhook-option-text">
                        <strong>failfast</strong> - Stop on first rule failure
                      </span>
                    </label>
                  </div>

                  <p className="webhook-url-hint">
                    Keep this URL secret. Use it to trigger pipeline validation from external systems.
                  </p>
                </div>
              )}

              {showHelp && (
                <div className="webhook-help-card">
                  <h4>How to use the webhook</h4>
                  <p>Send a POST request to the webhook URL with trade details:</p>
                  <pre className="webhook-help-code">
{`POST ${getWebhookUrl()}
Content-Type: application/json

{
  "direction": "LONG",
  "entryPrice": 1.0850,
  "stopPrice": 1.0820,
  "targetPrice": 1.0920,
  "riskPercent": 1.0
}`}
                  </pre>
                  <p>Response:</p>
                  <pre className="webhook-help-code">
{`{
  "success": true,
  "status": "PASSED",
  "instrument": "${pipeline.instrument}",
  "pipelineName": "${pipeline.name}",
  "rulesPassed": 3,
  "rulesFailed": 0,
  "remainingCalls": 95
}`}
                  </pre>
                  <p>
                    <strong>Options:</strong><br/>
                    <code>?details=true</code> - Include detailed rule execution results<br/>
                    <code>?failfast=true</code> - Stop validation on first failure
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineSettingsModal;
